// Cross-tab leader election was previously used so only one tab opened
// the WebSocket and fanned messages out to peers via BroadcastChannel.
// In practice it kept deadlocking new tabs as followers when a phantom
// leader (stale state from HMR, sleep/wake, or a pinned tab in another
// window) kept pinging — the WS would never connect on the fresh tab.
// Each tab now opens its own socket; this module is kept as a no-op
// shim so callers don't need to change.

export type TabLeaderOptions = {
  channelName?: string;
  tabId?: string;
  onLeaderChange?: (isLeader: boolean) => void;
};

export type TabLeaderHandle = {
  start: () => void;
  stop: () => void;
  isLeader: () => boolean;
  tabId: string;
};

export const createTabId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createTabLeader = (
  options: TabLeaderOptions = {}
): TabLeaderHandle => {
  const { tabId = createTabId(), onLeaderChange } = options;
  let started = false;

  return {
    start: () => {
      if (started) return;
      started = true;
      onLeaderChange?.(true);
    },
    stop: () => {
      started = false;
    },
    isLeader: () => started,
    tabId,
  };
};
